import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Home from "@/pages/Home";
import Wallet from "@/pages/Wallet";
import AjoSavings from "@/pages/AjoSavings";
import AjoGroupDetail from "@/pages/AjoGroupDetail";
import CreateAjoGroup from "@/pages/CreateAjoGroup";
import IndividualSavings from "@/pages/IndividualSavings";
import SavingsCalculator from "@/pages/SavingsCalculator";
import Activity from "@/pages/Activity";
import Profile from "@/pages/Profile";
import AdminDashboard from "@/pages/AdminDashboard";
import Help from "@/pages/Help";
import Pricing from "@/pages/Pricing";
import Analytics from "@/pages/Analytics";
import KYC from "@/pages/KYC";
import Challenges from "@/pages/Challenges";
import Achievements from "@/pages/Achievements";
import FutureLock from "@/pages/FutureLock";
import Rewards from "@/pages/Rewards";
import UpdatePrompt from "@/components/UpdatePrompt";
import PushSetup from "@/components/PushSetup";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user || !user.is_admin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Protected Routes */}
            <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
            <Route path="/ajo" element={<ProtectedRoute><AjoSavings /></ProtectedRoute>} />
            <Route path="/ajo/create" element={<ProtectedRoute><CreateAjoGroup /></ProtectedRoute>} />
            <Route path="/ajo/:groupId" element={<ProtectedRoute><AjoGroupDetail /></ProtectedRoute>} />
            <Route path="/savings" element={<ProtectedRoute><IndividualSavings /></ProtectedRoute>} />
            <Route path="/calculator" element={<ProtectedRoute><SavingsCalculator /></ProtectedRoute>} />
            <Route path="/activity" element={<ProtectedRoute><Activity /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/kyc" element={<ProtectedRoute><KYC /></ProtectedRoute>} />
            <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
            <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
            <Route path="/futurelock" element={<ProtectedRoute><FutureLock /></ProtectedRoute>} />
            <Route path="/rewards" element={<ProtectedRoute><Rewards /></ProtectedRoute>} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
        <UpdatePrompt />
        <PushSetup />
      </div>
    </AuthProvider>
  );
}

export default App;
